import get from 'lodash/get';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  FILE_STATES,
  NOTIFICATION_TYPES,
} from '@savgroup-front-common/constants';
import { Banner } from '@savgroup-front-common/core/atoms/Banners';
import {
  RadioButton,
  RadioButtonGroup,
} from '@savgroup-front-common/core/atoms/Form';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import { Paragraph } from '@savgroup-front-common/core/atoms/Paragraph';
import { SafeFormattedMessage } from '@savgroup-front-common/core/formatters';

import transitionFormMessages from '../../../../../components/DevicePasswordForm/messages';

import messages from './messages';
import {
  $ReturnProductFormContainer,
  $SubmitButton,
  $SubmitRow,
} from './ReturnProductForm.styles';

const warningOptions = [
  FILE_STATES.REQUESTED_RETURN_QUOTE_REJECTED,
  FILE_STATES.REQUEST_TO_RETURN_IN_THE_STATE,
];

const FIELDS = {
  SELECTED_TRANSITION: 'selectedTransition',
};

interface TargetState {
  name: string;
  [key: string]: any;
}

interface Transition {
  label: string;
  targetState: TargetState;
  [key: string]: any;
}

interface Action {
  transitions?: Transition[];
  [key: string]: any;
}

interface ReturnProductFormProps {
  actions?: Action[];
  onSubmit: (data: {
    selectedTransition: (Transition & Action) | undefined;
  }) => void;
  isModal?: boolean;
  closeModal?: () => void;
  isStateUpdatePending?: boolean;
}

const ReturnProductForm: React.FC<ReturnProductFormProps> = ({
  actions = [],
  onSubmit,
  isModal = false,
  closeModal,
  isStateUpdatePending = false,
}) => {
  const [selectedTransition, setSelectedTransition] = useState<
    (Transition & Action) | undefined
  >(undefined);

  const hasTransitions = actions.some(
    ({ transitions = [] }) => transitions.length > 0,
  );

  return (
    <$ReturnProductFormContainer>
      <Heading level={2}>
        <FormattedMessage {...messages.header} />
      </Heading>
      <Banner
        isOpen={!hasTransitions}
        notificationType={NOTIFICATION_TYPES.ERROR}
      >
        <SafeFormattedMessage
          id={messages.noOptions.id}
          defaultMessage={messages.noOptions.defaultMessage}
        />
      </Banner>
      <RadioButtonGroup name={FIELDS.SELECTED_TRANSITION}>
        {actions.map((action) => {
          const { transitions = [] } = action || {};

          return transitions.map((transition) => {
            const { label, targetState } = transition;

            const checked =
              targetState.name ===
              get(selectedTransition, ['targetState', 'name']);

            return (
              <RadioButton
                key={label}
                name={FIELDS.SELECTED_TRANSITION}
                isLocked={isStateUpdatePending}
                label={label}
                checked={checked}
                onClick={() => {
                  setSelectedTransition({ ...transition, ...action });
                }}
              />
            );
          });
        })}
      </RadioButtonGroup>

      {selectedTransition &&
        warningOptions.includes(selectedTransition.targetState.name as any) && (
          <Paragraph danger>
            <FormattedMessage {...messages.warning} />
          </Paragraph>
        )}

      <$SubmitRow>
        <$SubmitButton
          primary
          disabled={!selectedTransition || isStateUpdatePending}
          onClick={() => {
            onSubmit({
              selectedTransition,
            });
            if (isModal && closeModal) {
              closeModal();
            }
          }}
          isLoading={isStateUpdatePending}
        >
          <FormattedMessage {...transitionFormMessages.submit} />
        </$SubmitButton>
      </$SubmitRow>
    </$ReturnProductFormContainer>
  );
};

ReturnProductForm.displayName = 'ReturnProductForm';

export default ReturnProductForm;
